import { AddressComponentService } from '../services/address.component.service';
import { TogetherBaseAddress } from './../models/api.models';
import { UntypedFormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
    Component, OnInit, Input, Output,
    EventEmitter, OnChanges, SimpleChanges
} from '@angular/core';
import { FullAddress } from '../models/address.models';
import * as _ from 'lodash-es';

/**
 * @export
 * @class AddressComponent
 * @implements {OnInit}
 * @implements {OnChanges}
 */
@Component({
    selector: 'app-address',
    templateUrl: './address.component.html',
    providers: [AddressComponentService],
    standalone: false
})
export class AddressComponent implements OnInit, OnChanges {

    /**
     * form group of address lookup component
     * @type {FormGroup}
     * @memberof AddressComponent
     */
    @Input() public addressLookUp: UntypedFormGroup = this.fb.group({});


    /**
     *
     * @readonly
     * @memberof AddressComponent
     */
    @Input()
    get tad_has_search_store() {

        return this.hasSearched;
    }

    /**
     * @memberof AddressComponent
     */
    set tad_has_search_store(val) {
        let value = val;
        if (val instanceof UntypedFormControl) {
            value = val.value;
        }
        this.hasSearched.setValue(value);
    }

    /**
   *
   * @readonly
   * @memberof AddressComponent
   */
    @Input()
    get tad_hasAddress_selected_store() {
        return this.hasAddressSelected;
    }

    /**
     * @memberof AddressComponent
     */
    set tad_hasAddress_selected_store(val) {
        let value = val;
        if (val instanceof UntypedFormControl) {
            value = val.value;
        }
        this.hasAddressSelected.setValue(value);
    }


    /**
     *
     * @memberof AddressComponent
     */
    get tad_postcode_store() {
        if (this.status === 0) {
            this.addressLookUp.addControl('postcode', this.postcode);
        } else {
            this.addressLookUp.removeControl('hasSearched');
        }

        return this.postcode;
    }

    /**
     *
     * @memberof AddressComponent
     */
    set tad_postcode_store(val) {
        let value = val;

        if (val instanceof UntypedFormControl) {
            value = val.value;
        }
        this.postcode.setValue(value);
    }

    /**
     * @type {FormControl}
     * @memberof AddressComponent
     */
    @Input()
    hasSearched: UntypedFormControl = new UntypedFormControl(null, [
        Validators.required,
    ]);


    /**
     *
     *
     * @type {FormControl}
     * @memberof AddressLookupComponent
     */
    @Input()
    hasAddressSelected: UntypedFormControl = new UntypedFormControl('', [
        Validators.required,
    ]);

    /**
     * @type {FormControl}
     * @memberof AddressComponent
     */
    @Input()
    postcode: UntypedFormControl = new UntypedFormControl(null, [
        Validators.required,
        // tslint:disable-next-line:max-line-length
        Validators.pattern(/^([A-Z](([0-9][0-9]?)|([A-Z][0-9][0-9]?)|([A-Z]?[0-9][A-Z])) ?[0-9][ABD-HJLNP-UW-Z]{2})$/i),
    ]);

    /**
    * The status of the component
    *  0 - Postcode search
    *  1 - Display address
    *  2 - Edit address
    *
    * @type {string}
    * @memberof AddressComponent
    */
    status: number;

    /**
     * Incoming address that the address component manipulates.
     *
     * @type {TogetherBaseAddress}
     * @memberof AddressComponent
     */
    @Input() incomingAddress: TogetherBaseAddress = new TogetherBaseAddress();

    /**
     * The iterator of the address. This is required because
     *  the iterator MUST be re-inserted back where it was.
     *
     * @type {number}
     * @memberof AddressComponent
     */
    @Input() iterator: number = 1;

    /**
    * Incomming address from parent
    *
    * @type {number}
    * @memberof AddressEditorComponent
    */
    @Input() index: number = 0;

    /**
     * The outgoing address selected / looked up.
     *
     * @type {EventEmitter<TogetherBaseAddress>}
     * @memberof AddressComponent
     */
    @Output() outgoingAddress: EventEmitter<object> = new EventEmitter<object>();

    /**
     * Outputs the form group so that the validity of
     *  the form can be checked and included within existing forms.
     *
     * @type {EventEmitter<FormGroup>}
     * @memberof AddressComponent
     */
    @Output() form_out: EventEmitter<UntypedFormGroup> = new EventEmitter<UntypedFormGroup>();

    /**
    * Outputs the form group so that the validity of
    * the form can be checked and included within existing forms.
    *
    * @type {EventEmitter<FormGroup>}
    * @memberof AddressComponent
    */
    @Output() lookup_form_out: EventEmitter<UntypedFormGroup> = new EventEmitter<UntypedFormGroup>();

    // HERE
    /**
     * Handles form incoming from the editor component, re-emits the form back out to subscriber.
     *
     * @param {any} form_group
     * @memberof AddressComponent
     */
    _handleIncomingForm(form_group) {
        this.form_out.emit(form_group);
    }

    handleLookupForm(form_group) {
        this.lookup_form_out.emit(form_group);
    }

    /**
     * Signal from child component to update outgoing address.
     *
     * @param {any} $event
     * @memberof AddressComponent
     */
    addressUpdate($event): void {
        if ($event.valid) {
            this.outgoingAddress.emit({
                address: this.service.address,
                form: $event.form,
            });
        }
    }

    hasEditedUnsavedAddress() {
        // If the address is new
        const values = [
            _.isEmpty(this.incomingAddress.County),
            _.isEmpty(this.incomingAddress.District),
            _.isEmpty(this.incomingAddress.HouseName),
            _.isEmpty(this.incomingAddress.HouseNumber),
            _.isEmpty(this.incomingAddress.Street1),
            _.isEmpty(this.incomingAddress.Street2),
            _.isEmpty(this.incomingAddress.PostTown),
            _.isEmpty(this.incomingAddress.Postcode),
        ];

        return values.indexOf(false) !== -1;
    }

    /**
     * @returns
     * @memberof AddressComponent
     */
    hasIncomingAddress() {
        return (
            _.isNil(this.incomingAddress) === false &&
            _.isNil(this.incomingAddress['Id']) === false &&
            this.incomingAddress['Id'] !== '00000000-0000-0000-0000-000000000000') ||
            (_.isNil(this.incomingAddress) === false &&
                _.isNil(this.incomingAddress['AddressId']) === false &&
                this.incomingAddress['AddressId'] !== '00000000-0000-0000-0000-000000000000' &&
                this.incomingAddress['AddressId'] !== 0);
    }

    /**
     * Creates an instance of AddressComponent.
     * @param {AddressComponentService} service
     * @memberof AddressComponent
     */
    constructor(private service: AddressComponentService, private fb: UntypedFormBuilder) {

        this.service.controlMode.subscribe((next) => {
            this.status = next;
            if (next === 0) {
                this.addressLookUp.addControl('hasSearched', this.tad_has_search_store);
                this.addressLookUp.addControl('hasAddressSelected', this.tad_hasAddress_selected_store);
                this.addressLookUp.addControl('postcode', this.tad_postcode_store);
            } else {
                this.addressLookUp.removeControl('hasSearched');
                this.addressLookUp.removeControl('postcode');
                this.addressLookUp.removeControl('hasAddressSelected');
            }

        });
    }

    /**
     * Initialises incoming address.
     *
     * @memberof AddressComponent
     */
    ngOnInit() {
        this.service.address = this.incomingAddress;
        if (this.hasIncomingAddress() || this.hasEditedUnsavedAddress()) {
            this.setStatus(1);
            this.setEditMode(1);
        } else {
            this.setStatus(0);
            this.setEditMode(0);
        }
    }

    /**
     * @param {SimpleChanges} changes
     * @memberof AddressComponent
     */
    ngOnChanges(changes: SimpleChanges) {
        if (_.isNil(changes['incomingAddress']) === false) {
            const address = changes['incomingAddress'];
            if (_.isNil(address.currentValue) === false &&
                address.previousValue !== address.currentValue) {
                this.setStatus(1);
                this.setEditMode(1);
            }
        }
    }

    /**
     * @param {number} val
     * @memberof AddressComponent
     */
    setEditMode(val: number): void {
        this.service.editMode = val;
    }

    /**
     * Sets status.
     *
     * @param {number} val
     * @memberof AddressComponent
     */
    setStatus(val: number): void {
        if (_.inRange(val, 3)) {
            this.service.controlMode.next(val);
        }
    }
}
